import React from 'react';
import Icon from '../../../components/AppIcon';

const ActivityTimeline = ({ activities }) => {
  const getActivityIcon = (activity) => {
    if (activity?.completedAt) {
      return { name: 'CheckCircle2', color: 'text-success', bgColor: 'bg-success/10' };
    }
    if (activity?.completionPercentage > 0) {
      return { name: 'PlayCircle', color: 'text-blue-500', bgColor: 'bg-blue-500/10' };
    }
    return { name: 'Circle', color: 'text-muted-foreground', bgColor: 'bg-muted' };
  };

  const getActivityMessage = (activity) => {
    if (activity?.completedAt) {
      // Use score_percentage from answers_data if available, otherwise fall back to completionPercentage
      const scorePercentage = activity?.answersData?.score_percentage ?? activity?.completionPercentage;
      return `Completed "${activity?.learningTrails?.title || 'Trail'}" with ${scorePercentage}% score`;
    }
    if (activity?.completionPercentage > 0) {
      return `Working on "${activity?.learningTrails?.title || 'Trail'}" - ${activity?.completionPercentage}% complete`;
    }
    return `Started "${activity?.learningTrails?.title || 'Trail'}"`;
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    }
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    }
    if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
    return activityTime?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!activities || activities?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center shadow-lg">
            <Icon name="Activity" size={20} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Recent Activity</h2>
            <p className="text-sm text-muted-foreground">Your learning history</p>
          </div>
        </div>
        <div className="text-center py-8">
          <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-sm text-muted-foreground">No activity yet. Start a trail to see your progress here!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center shadow-lg">
          <Icon name="Activity" size={20} className="text-white" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Recent Activity</h2>
          <p className="text-sm text-muted-foreground">Your learning history</p>
        </div>
      </div>

      <div className="space-y-4">
        {activities?.slice(0, 5)?.map((activity) => {
          const iconInfo = getActivityIcon(activity);
          return (
            <div key={activity?.id} className="flex items-start space-x-3 pb-4 border-b border-border last:border-0">
              <div className={`w-8 h-8 ${iconInfo?.bgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
                <Icon name={iconInfo?.name} size={16} className={iconInfo?.color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground">{getActivityMessage(activity)}</p>
                <p className="text-xs text-muted-foreground mt-1">{getTimeAgo(activity?.lastAccessedAt)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActivityTimeline;